/** What the year-on-year bridge prints, as strings (S1-02).
 *
 * The screen used to round each tonnage on its own, from a value the server had already rounded,
 * so about one cell in twenty was 0.1 t off and the parts did not add up: `1.02 - 1.00` beside a
 * delta of `0.01`. This is the one place the bridge decides how a cell is written.
 *
 * - **Every cell goes through `quantity`** (`lib/format.ts` -> `roundDecimal`): one rounding
 *   function, applied once, to the exact string the API sent. Nothing here rounds.
 * - **A group that must add up is printed at the fewest places at which it does** (`placesThatFoot`),
 *   never nudged. The bridge is `prior + explained + unexplained + residual = current`; a site's
 *   row is `prior + delta = current`. Where one decimal is enough, one is printed - which is almost
 *   every cell - and where it is not, that group alone shows another, so a reader can add up what
 *   they see. Largest-remainder allocation is refused on purpose: it makes the parts foot by
 *   changing a figure to one it does not have.
 */

import { placesThatFoot } from "../lib/decimal";
import { quantity } from "../lib/format";
import { signedTonnes } from "./reportsFormat";

/** The fewest places a tonnage is ever printed to: the unit both regimes report in, to a tenth. */
export const BASE_PLACES = 1;

export interface BridgeMovementInput {
  prior_tonnes: string;
  current_tonnes: string;
  delta_tonnes: string;
}

export interface BridgeInput {
  prior_total_tonnes: string;
  explained_tonnes: string;
  unexplained_tonnes: string;
  residual_tonnes: string;
  current_total_tonnes: string;
  movements: readonly BridgeMovementInput[];
  /** The sites the residual is made of (below the listing threshold). Absent only from a caller
   * that does not have them; the screen then offers no list rather than an empty one. */
  residual_sites?: readonly BridgeMovementInput[];
}

export interface BridgeRowCells {
  places: number;
  prior: string;
  current: string;
  /** Signed. */
  delta: string;
}

export interface BridgeCells {
  /** Places the five headline cells are printed to; more than `BASE_PLACES` only when one
   * decimal would have made the parts disagree with the total. */
  places: number;
  prior: string;
  /** Signed. */
  explained: string;
  /** Signed. */
  unexplained: string;
  /** Signed. */
  residual: string;
  current: string;
  rows: readonly BridgeRowCells[];
  /** The residual's sites, each written like a movement row. Empty when there are none or the
   * response did not carry them. */
  residualRows: readonly BridgeRowCells[];
}

/** A residual site moved by less than a tenth of a tonne by definition, so at one decimal every
 * delta would read `0.0` and the list would say nothing. Printed to a kilogram instead. */
export const RESIDUAL_PLACES = 3;

function rowCells(movement: BridgeMovementInput, basePlaces: number = BASE_PLACES): BridgeRowCells {
  const rowPlaces = placesThatFoot(
    [movement.prior_tonnes, movement.delta_tonnes],
    movement.current_tonnes,
    basePlaces,
  );
  return {
    places: rowPlaces,
    prior: quantity(movement.prior_tonnes, rowPlaces),
    current: quantity(movement.current_tonnes, rowPlaces),
    delta: signedTonnes(movement.delta_tonnes, rowPlaces),
  };
}

export function bridgeCells(data: BridgeInput): BridgeCells {
  const places = placesThatFoot(
    [data.prior_total_tonnes, data.explained_tonnes, data.unexplained_tonnes, data.residual_tonnes],
    data.current_total_tonnes,
    BASE_PLACES,
  );
  return {
    places,
    prior: quantity(data.prior_total_tonnes, places),
    explained: signedTonnes(data.explained_tonnes, places),
    unexplained: signedTonnes(data.unexplained_tonnes, places),
    residual: signedTonnes(data.residual_tonnes, places),
    current: quantity(data.current_total_tonnes, places),
    rows: data.movements.map((movement) => rowCells(movement)),
    residualRows: (data.residual_sites ?? []).map((site) => rowCells(site, RESIDUAL_PLACES)),
  };
}

// ------------------------------------------------------------------------ the sites behind a total

/** A listed movement, as far as naming its site and saying whether a boundary change explains it. */
export interface BridgeSite extends BridgeMovementInput {
  facility_id: string;
  facility_name: string;
  /** The server's own sentence when a boundary change explains the movement; null or absent when
   * none does. This is the field that decided which total the movement was counted in. */
  because?: string | null;
}

export type SitesTotal = "explained" | "unexplained";

/** Which window a register of these sites should open on: this year, unless the sites hold nothing
 * this year and something last year.
 *
 * **Derived from the sites' own figures, never chosen by the link.** A site sold last year has
 * figures last year and none this year, and the boundary-change total is made of exactly such
 * sites; a register opened on this year for them would be empty, which reads as "nothing behind
 * this total".
 */
export type SitesWindow = "current" | "prior";

export interface SitesBehind {
  /** The facility ids the total is a sum of, in the order the bridge lists them. */
  ids: string[];
  window: SitesWindow;
}

/** The sites an explained or unexplained total is the sum of, from the bridge's own movements.
 *
 * `explained_tonnes` is the movements a boundary change explains and `unexplained_tonnes` the rest
 * (the same `because` the list below it narrows by), so the sites are read off the movements and
 * not copied into a second list that could disagree with them. Empty when none moved that way.
 */
export function sitesBehind(
  data: { movements: readonly BridgeSite[] },
  which: SitesTotal,
): SitesBehind {
  const sites = data.movements.filter((movement) =>
    which === "explained" ? Boolean(movement.because) : !movement.because,
  );
  const nonZero = (value: string) => Number(value) !== 0;
  const current = sites.some((site) => nonZero(site.current_tonnes));
  const prior = sites.some((site) => nonZero(site.prior_tonnes));
  return {
    ids: sites.map((site) => site.facility_id),
    window: !current && prior ? "prior" : "current",
  };
}
